<template>
  <div
    class="c-feed-list-item-row"
    :class="{
      'c-feed-list-item-row--disabled': sourceDisabled,
      'c-feed-list-item-row--offline': !!offlinePartner,
    }"
  >
    <div class="c-feed-list-item-row-value">
      <div class="left-content flex">
        <div v-if="sourceDisabled" :style="{ paddingRight: '5px' }">
          <FontAwesomeIcon
            :icon="['fak', 'loading']"
            :style="{ height: '18px', width: '18px', color: 'currentColor' }"
            class="text-neutral-border mr-8 animate-spin"
          />
        </div>
        <DataSourceAvatar
          v-if="!!offlinePartner && feedIntegrationType"
          :data-source="feedIntegrationType"
          size="x-small"
          class="mr-8"
        />
        <span
          class="c-feed-list-item-row__source-name mr-4"
          :class="{
            'text-neutral-text-placeholder': sourceDisabled,
          }"
        >
          {{ source.table }}
        </span>
        <span
          v-if="source.created_at"
          :class="{
            'text-neutral-text-placeholder': sourceDisabled,
            'ml-2 text-neutral-text-weak': !sourceDisabled,
          }"
        >
          · {{ date }}
        </span>
        <BittsBadge
          v-if="sourceErrored"
          status="danger"
          text="Error"
          class="ml-8"
        />
        <BittsBadge
          v-if="sourceDeleting"
          status="danger"
          text="Deleting"
          class="ml-8"
        />
        <BittsBadge
          v-else-if="hasMissingRequiredCells || sourceWarning"
          status="warning"
          text="Warning"
          class="ml-8"
        />
      </div>
      <div class="flex">
        <div v-if="canManageDatasource" class="c-feed-list-item-row-value">
          <!-- For offline partners, show the action buttons -->
          <template v-if="offlinePartner">
            <BittsButton
              v-if="displayMapFieldsButton && source.id"
              class="c-feed-list-item-row__map-fields"
              text="Map Fields"
              size="x-small"
              type="primary"
              variant="ghost"
              @click="showMapFields(source.id)"
            />
            <BittsButton
              v-if="allowAddData && source.id"
              :disabled="sourceDisabled"
              :left-icon="['fak', 'add']"
              text="Add Data"
              size="x-small"
              type="primary"
              variant="ghost"
              data-testid="add-data-to-source-button"
              @click="showCSVUploadModalForSource(source.id)"
            />
            <BittsButton
              :disabled="sourceDeleting"
              text="Remove"
              size="x-small"
              type="danger"
              variant="ghost"
              @click="showDeleteSourceModal(source)"
            />
          </template>
          <!-- For regular data sources, show the settings button -->
          <BittsButton
            v-else
            :center-icon="['fak', 'settings']"
            type="neutral"
            variant="outline"
            size="x-small"
            aria-label="Settings"
            @click="() => gotToSettings(feed, source)"
            class="ml-4"
          />
        </div>
      </div>
    </div>
    <BittsAlert
      v-if="sourceErrored || sourceWarning"
      v-bind="sourceDetails"
      class="c-feed-list-item-row__problem"
    />
    <BittsAlert
      v-else-if="hasMissingRequiredCells"
      message="Blank required cells detected"
      description="Rows with blank required cells will be skipped"
      class="c-feed-list-item-row__problem"
      color="warning"
    />
  </div>
</template>

<script lang="ts" setup>
import { BittsAlert, BittsBadge, BittsButton } from '@crossbeam/bitts';

import { DateTime } from 'luxon';
import { computed } from 'vue';
import { useRouter } from 'vue-router';

import DataSourceAvatar from '@/components/DataSourceAvatar.vue';

import useAuth from '@/composables/useAuth';
import useFieldMappingVisibility from '@/composables/useFieldMappingVisibility';
import {
  FILE_UPLOAD_DATA_SOURCE_TYPE,
  GOOGLE_SHEETS_DATA_SOURCE_TYPE,
  SOURCE_STATUS_MAP,
  getDataSourceLabel,
} from '@/constants/data_sources';
import {
  BIG_CSV_FILE_LIMIT_500MB,
  TEMP_ALLOW_ADDING_TO_BIG_CSV,
} from '@/constants/feature_flags';
import { todayYesterdayTomorrowOrCustom } from '@/date_time_utils';
import { useFeatureFlagStore } from '@/stores';
import { Feed } from '@/types/feeds';
import { OfflinePartner } from '@/types/partners';
import { Source } from '@/types/sources';

import { DataSourceSettingsTabType } from './SettingsModal/constants';

const props = defineProps<{
  source: Source;
  /* Offline partner sources can outlive their feed in a stale refresh, so the
     row has to tolerate the lookup missing rather than blanking the whole tab */
  feed?: Feed;
  offlinePartner?: OfflinePartner;
  uploadedByEmail?: string;
}>();

const { hasFeatureFlag } = useFeatureFlagStore();

const { hasPermission } = useAuth();
const router = useRouter();

async function gotToSettings(feed: Feed | undefined, source: Source) {
  if (!feed) return;
  await router.push({
    name: 'data-sources-settings-new',
    params: {
      id: feed.id,
      source_id: source.id,
      tab: DataSourceSettingsTabType.GENERAL,
    },
  });
}

const feedIntegrationType = computed(() => props.feed?.integration.type);

const isCSVFeed = computed(() => {
  return feedIntegrationType.value === FILE_UPLOAD_DATA_SOURCE_TYPE;
});

const feedLabel = computed(() =>
  props.feed
    ? getDataSourceLabel(
        props.feed.integration.type,
        props.feed.integration.friendly_name,
      )
    : 'data source',
);

/* The error copy names the artifact the user edits rather than the feed, so it
   cannot reuse getDataSourceLabel. Only CSV and Sheets feeds reach this row
   (via CsvFeed, GoogleSheetsFeed and offline partners), so anything else means
   the feed lookup missed and the generic noun is the honest fallback. */
const FeedErrorNoun: Record<string, string> = {
  [FILE_UPLOAD_DATA_SOURCE_TYPE]: 'CSV File',
  [GOOGLE_SHEETS_DATA_SOURCE_TYPE]: 'Google Sheet',
};

const feedErrorNoun = computed(
  () =>
    (feedIntegrationType.value && FeedErrorNoun[feedIntegrationType.value]) ||
    'data source',
);

function isStatusInCategory(
  status: string | null | undefined,
  category: readonly string[],
): boolean {
  return !!status && category.includes(status);
}

const sourceErrored = computed(() => {
  /* For Google Sheets specifically, we want to
      show an error on the feed if any of the sources are errored.
      Errors on sources supercede errors on feeds and cause the
      feed to be in an error state. */

  /* The missing_required property isn't counted as an error. During a refactor
      of the API this should all be simplified + changed */
  return (
    isStatusInCategory(props.source.status, SOURCE_STATUS_MAP.error) &&
    !hasMissingRequiredCells.value
  );
});

const sourceWarning = computed(() => {
  /* Missing required cells is a warning */
  return isStatusInCategory(props.source.status, SOURCE_STATUS_MAP.warning);
});

const sourceDeleting = computed(() => {
  return isStatusInCategory(props.source.status, SOURCE_STATUS_MAP.deleting);
});

const sourceDisabled = computed(() => {
  return (
    isStatusInCategory(props.source.status, SOURCE_STATUS_MAP.process) ||
    isStatusInCategory(props.source.status, SOURCE_STATUS_MAP.deleting)
  );
});
const hasMissingRequiredCells = computed(() => {
  /* Missing required cells is a warning. This is confusing and should
        be cleaned up in an API refactor */
  const missingCount = props.source?.properties?.row_errors?.missing_required;
  return !!missingCount;
});
const canManageDatasource = computed(() => {
  return props.offlinePartner
    ? hasPermission('manage:offline-partners')
    : hasPermission('manage:data-sources');
});
const date = computed(() => {
  if (!props.source.created_at) return '';
  const formattedDate = formatDate(props.source.created_at);
  if (props.uploadedByEmail) {
    return `${formattedDate} by ${props.uploadedByEmail}`;
  }
  return formattedDate;
});
const sourceDetails = computed(() => {
  return {
    color: (sourceErrored.value ? 'error' : 'warning') as 'error' | 'warning',
    message: sourceErrored.value
      ? `We're having trouble with this ${feedErrorNoun.value}`
      : `Some rows in your ${feedLabel.value} have been skipped`,
    description: props.source.error_message || undefined,
  };
});
const hasBigCSVFileLimit = computed(() => {
  return hasFeatureFlag(BIG_CSV_FILE_LIMIT_500MB);
});
const allowAddingToBigFiles = computed(() => {
  return hasFeatureFlag(TEMP_ALLOW_ADDING_TO_BIG_CSV);
});
const sourceFields = computed(() => props.source.fields);
const { displayMapFieldsButton } = useFieldMappingVisibility(sourceFields);

const allowAddData = computed(() => {
  return (
    isCSVFeed.value &&
    (!hasBigCSVFileLimit.value || allowAddingToBigFiles.value)
  );
});

function formatDate(asIso: string) {
  const formatter = (d: DateTime) => d.toFormat("D 'at' t");
  return todayYesterdayTomorrowOrCustom(
    DateTime.fromISO(asIso),
    true,
    formatter,
  );
}
async function showCSVUploadModalForSource(sourceId: number) {
  await router.push({
    name: props.offlinePartner
      ? 'offline-file-upload-add-data'
      : 'file-upload-add-data',
    params: { id: sourceId },
    query: props.offlinePartner
      ? {
          offlinePartnerUuid: props.offlinePartner.uuid,
          offlinePartnerId: props.offlinePartner.id,
        }
      : undefined,
  });
}

async function showDeleteSourceModal(sourceToDelete: Source) {
  if (!sourceToDelete.id) return;

  await router.push({
    name: props.offlinePartner ? 'offline-delete-source' : 'delete-source',
    params: { source_id: sourceToDelete.id },
    query: props.offlinePartner
      ? {
          offlinePartnerUuid: props.offlinePartner.uuid,
          offlinePartnerId: props.offlinePartner.id,
        }
      : undefined,
  });
}

async function showMapFields(sourceId: number) {
  await router.push({
    name: props.offlinePartner
      ? 'offline-map-fields-columns'
      : 'map-fields-columns',
    params: { id: sourceId },
    query: props.offlinePartner
      ? {
          offlinePartnerUuid: props.offlinePartner.uuid,
          offlinePartnerId: props.offlinePartner.id,
        }
      : undefined,
  });
}
</script>

<style lang="pcss" scoped>
@reference "../../css/theme.css";
.c-feed-list-item-row {
  @apply flex flex-col justify-between py-12 px-16 border-neutral-border border-t;
}

.c-feed-list-item-row-value {
  @apply flex justify-between;
}

.c-feed-list-item-row--disabled {
  @apply bg-neutral-100 border-neutral-border;
}

.c-feed-list-item-row__problem {
  @apply mt-12;
}
</style>
